import { Slot, Slottable } from '@radix-ui/react-slot';
import { memo, use, useState, useSyncExternalStore } from 'react';

import { cn } from '@veraclins-dev/utils';

import { type IconName } from '../icons';
import { type WithTooltip } from '../types';

import { DisabledTooltipContext } from './contexts/disabled-tooltip-context';
import { type ButtonVariants, buttonVariants } from './utils/variants/button';
import { tooltipTriggerClassName } from './utils/variants/disabled-trigger';
import { extractStyleProps } from './utils/variants/styles';
import { Icon } from './icon';
import { Spinner } from './spinner';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from './tooltip';

// Held in a variable rather than written inline for the same reason as in
// `spinner.tsx`: a literal `role="status"` trips oxlint's prefer-tag-over-role,
// which would nudge this toward `<output>` (a form-output element, wrong
// semantics for a loading wait).
const statusRole = 'status';

export type ButtonEmphasis = ButtonVariants['emphasis'];
export type ButtonTone = ButtonVariants['tone'];
export type ButtonSize = ButtonVariants['buttonSize'];

interface Props extends React.ComponentProps<'button'>, ButtonVariants {
  asChild?: boolean;
  type?: 'button' | 'submit' | 'reset';
  'aria-label'?: string;
  'data-disabled'?: 'true' | 'false';
  loadingIcon?: IconName;
  leadingIcon?: IconName;
  trailingIcon?: IconName;
}

interface BaseProps extends Props {
  /**
   * Set on a disabled or loading <button> that must stay focusable: it gets
   * the inert handlers instead of the DOM `disabled` attribute.
   */
  focusableWhenDisabled?: boolean;
}

function block(event: React.SyntheticEvent) {
  event.preventDefault();
  event.stopPropagation();
}

function blockActivationKey(event: React.KeyboardEvent) {
  if (event.key === 'Enter' || event.key === ' ') block(event);
}

/**
 * Makes an element inert where the DOM `disabled` attribute is not used: an
 * `asChild` child (a disabled link would still follow its `href`) and a
 * <button> kept focusable for its tooltip. Blocking the click also blocks form
 * submission. Pointer down is cancelled but keeps propagating, as it does from
 * a natively disabled button: the press does not focus the element, Radix
 * triggers skip a cancelled event, and an open popover still closes.
 */
const inertHandlers = {
  onClickCapture: block,
  onAuxClickCapture: block,
  onKeyDownCapture: blockActivationKey,
  onKeyUpCapture: blockActivationKey,
  onPointerDownCapture: (event: React.PointerEvent) => event.preventDefault(),
};

/**
 * A flexible button component for the design system, built on the Model A action
 * axes: `emphasis` (filled | soft | outline | quiet | ghost) is the treatment
 * and `tone` (neutral | brand | danger | success) is the colour. A bare `<Button>` is a `filled`
 * `neutral` button; the brand CTA is `<Button tone="brand">`. It can render as a
 * Radix Slot child component for better integration with other components, and
 * supports loading states with a spinner, leading and trailing icons, and
 * accessibility features.
 */
function Base({
  className,
  emphasis,
  tone,
  buttonSize,
  asChild = false,
  type = 'button',
  loading = false,
  loadingIcon,
  leadingIcon,
  trailingIcon,
  children,
  disabled,
  focusableWhenDisabled = false,
  tabIndex,
  ...props
}: BaseProps) {
  const Comp = asChild ? Slot : 'button';
  const isInertChild = asChild && Boolean(disabled || loading);
  const isIconOnly =
    buttonSize === 'icon' && !children && !leadingIcon && !trailingIcon;
  if (isIconOnly && !props['aria-label'] && !props['aria-labelledby']) {
    console.warn(
      'Icon-only buttons require an aria-label or aria-labelledby for accessibility.',
    );
  }
  return (
    <Comp
      data-slot="button"
      className={className}
      type={asChild ? undefined : type}
      // Slot's child may be an <a>, which has no `disabled`; it and a
      // focusable button get aria-disabled and the inert handlers instead.
      disabled={asChild || focusableWhenDisabled ? undefined : disabled}
      {...props}
      // `href` stays on a disabled link: without it the link role, which
      // aria-disabled is announced on, is gone.
      tabIndex={tabIndex ?? (isInertChild ? -1 : undefined)}
      {...(isInertChild || focusableWhenDisabled ? inertHandlers : {})}
    >
      {loading &&
        (loadingIcon ? (
          // The same structure as Spinner: the wrapper is the live status
          // region named "Loading", and the glyph is decoration.
          <span
            data-slot="button-loading-status"
            role={statusRole}
            aria-live="polite"
            aria-label="Loading"
            className="inline-flex items-center"
          >
            <Icon
              name={loadingIcon}
              aria-hidden="true"
              className="animate-spin motion-reduce:[animation-duration:2s]"
            />
          </span>
        ) : (
          <Spinner tone="current" />
        ))}
      {leadingIcon && <Icon name={leadingIcon} />}
      {/* Under `asChild` this marks the caller's element as the slot target,
          so the icons ride alongside it instead of tripping Slot's
          single-child rule. */}
      <Slottable>{children}</Slottable>
      {trailingIcon && <Icon name={trailingIcon} />}
    </Comp>
  );
}

const noSubscribe = () => () => undefined;
// Hoisted so its identity is stable: a new `getSnapshot` on every render makes
// React re-run its check effect for every Button in the tree.
const hydrated = () => true;

/**
 * `wanted`, but only once the page has hydrated and the inert handlers are
 * attached. Only a button that wants it reports `false` on the server: a
 * snapshot that already matches the client would not re-render every other
 * button after hydration.
 */
function useFocusableAfterHydration(wanted: boolean) {
  return useSyncExternalStore(noSubscribe, hydrated, () => !wanted) && wanted;
}

// Radix ignores touch, so a touch gesture stays uncancelled for ancestors that
// scroll or drag.
const blockEmptyOpen = (event: React.FocusEvent | React.PointerEvent) => {
  if ('pointerType' in event && event.pointerType === 'touch') return;
  event.preventDefault();
};

function Component({
  tooltip,
  emphasis,
  tone,
  buttonSize,
  surface,
  loading = false,
  loadingIcon,
  fullWidth = false,
  disabled,
  className,
  noRing = false,
  ...props
}: ButtonProps) {
  const { styleProps, others } = extractStyleProps(props);
  const isDisabled = Boolean(disabled || loading);
  // A disabled button with tooltip content stays focusable, so keyboard and
  // screen reader users can reach the reason it is unavailable. The server
  // HTML and the hydration render stay natively disabled: until the inert
  // handlers are attached, nothing else stops a submit button. A client-only
  // mount has no hydration render, so it is focusable from the first one.
  const hasTooltip = Boolean(tooltip) || use(DisabledTooltipContext);
  const wantsFocusable = isDisabled && hasTooltip && !others.asChild;
  const focusableWhenDisabled = useFocusableAfterHydration(wantsFocusable);
  // The root stays controlled, and it closes when its content goes away so it
  // cannot reopen from a stale state when content returns.
  const [open, setOpen] = useState(false);
  if (open && !tooltip) setOpen(false);
  const classes = cn(
    buttonVariants({
      emphasis,
      tone,
      buttonSize,
      surface,
      loading,
      className,
      fullWidth,
      noRing,
      ...styleProps,
    }),
  );
  // A natively disabled <button> needs no aria-disabled on top. An asChild
  // element (e.g. an <a>) and a focusable disabled button need the ARIA state.
  // A caller's own aria-disabled (a focusable but unavailable control) is kept.
  const ariaDisabled =
    isDisabled && (others.asChild || focusableWhenDisabled)
      ? 'true'
      : others['aria-disabled'];
  const baseProps = {
    ...others,
    className: classes,
    buttonSize,
    loading,
    loadingIcon,
    emphasis,
    tone,
    'aria-disabled': ariaDisabled,
    // Base decides whether this reaches the element as the DOM attribute, so
    // it is passed either way: a tooltip still has to know the trigger is
    // disabled to give it back its pointer events.
    disabled: isDisabled,
    focusableWhenDisabled,
    'data-disabled': isDisabled ? ('true' as const) : undefined,
  };
  // The trigger wrapper is always rendered, so `Base` keeps its place in the
  // tree and a tooltip arriving later does not remount the button and drop its
  // focus. Without tooltip content the trigger never opens, and hovering it
  // must not close a neighbour's tooltip (opening one closes the others).
  const keepClosed = tooltip ? undefined : blockEmptyOpen;
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild onPointerMove={keepClosed} onFocus={keepClosed}>
        <Base
          {...baseProps}
          className={tooltip ? tooltipTriggerClassName(baseProps) : classes}
        />
      </TooltipTrigger>
      {tooltip && (
        <TooltipContent>
          {tooltip}
          <TooltipArrow height={8} width={16} className="fill-neutral-muted" />
        </TooltipContent>
      )}
    </Tooltip>
  );
}

/**
 * A memoized flexible button component for the design system, built on the
 * Model A action axes `emphasis` and `tone`.
 * @category interactive-elements
 * @param {Props} props - Component props
 * @param {ButtonEmphasis} [props.emphasis='filled'] - Treatment (filled, soft, outline, quiet, ghost).
 *   `quiet` is a hairline `border` around a label in the main text colour:
 *   quieter than `outline`, louder than `ghost`.
 * @param {ButtonTone} [props.tone='neutral'] - Colour intent (neutral, brand, danger, success)
 * @param {ButtonSize} [props.buttonSize='md'] - Button size (sm, md, lg, xl, icon).
 *   sm, md, lg and xl are at least 32, 40, 48 and 56px tall, the same as the
 *   matching `inputSize`, so a button lines up with a field beside it.
 * @param {boolean} [props.asChild=false] - Render as a child component using Radix Slot.
 *   When `disabled` or `loading`, the child is made inert: it leaves the tab
 *   order (unless you pass `tabIndex`), and click, middle-click, Enter and
 *   Space are prevented before your handlers or the child's bubble-phase
 *   handlers run. A mouse press does not focus it. A link keeps its `href`,
 *   so it is still announced as a disabled link.
 * @param {boolean} [props.disabled=false] - Disable the button. Without tooltip
 *   content it gets the DOM `disabled` attribute and leaves the tab order. With
 *   tooltip content (`tooltip`, or a `StatusButton` message) it stays focusable
 *   so keyboard and screen reader users can read the tooltip: it gets
 *   `aria-disabled="true"` instead, and click, Enter, Space and form
 *   submission (including Enter in a text field) do nothing and do not call
 *   your handlers. A mouse press does not focus it, as with a native disabled
 *   button, and an open popover still closes on that press.
 *   Server HTML stays natively disabled until the page hydrates. Taking the
 *   tooltip content away while the button is still disabled makes it natively
 *   disabled again, and the browser drops focus to the document body, so keep
 *   the content for as long as the button is disabled. With `asChild`, tooltip
 *   content does not bring the child back into the tab order.
 * @param {boolean} [props.loading=false] - Show loading state with a spinner.
 *   A loading button is disabled, as described for `disabled`.
 * @param {React.ReactNode} [props.tooltip] - Tooltip content shown on hover and
 *   keyboard focus, including while the button is disabled or loading.
 * @param {IconName} [props.loadingIcon] - Custom icon to use for loading state
 * @param {boolean} [props.noRing=false] - Replace the focus ring with a solid outline that shows on keyboard focus only
 * @param {IconName} [props.leadingIcon] - Icon to display before the button content
 * @param {IconName} [props.trailingIcon] - Icon to display after the button content
 * @param {string} [props.type='button'] - Button type (button, submit, reset)
 * @param {string} [props.aria-label] - Accessible label. An icon-only button
 *   needs this or `aria-labelledby`: Button does not derive a name from its icon.
 * @param {React.Ref<HTMLButtonElement>} [props.ref] - Ref for the button element
 * @example
 * <Button emphasis="filled" tone="danger" buttonSize="lg" leadingIcon="trash">
 *   Delete
 * </Button>
 * @example
 * <Button asChild disabled>
 *   <a href="/billing">Manage billing</a>
 * </Button>
 * @example
 * <Button buttonSize="icon" aria-label="Notifications">
 *   <Icon name="bell" />
 * </Button>
 * @example
 * <Button type="submit" disabled tooltip="Accept the terms first">
 *   Submit
 * </Button>
 */
const Button = memo(Component);

type ButtonProps = WithTooltip<Props>;

export { Button, type ButtonProps, type ButtonVariants };

// for storybook
export default Component;
